import { useCallback, useRef, useState } from "react";
import type { BulkUploadPlan, PlannedAppointmentRow } from "./bulk-upload-plan";
import type { BulkUploadResult } from "./bulk-upload-import";
import { createMissingUnitsForPlan } from "./bulk-upload-units";

export interface UploadResult {
  success: boolean;
  message: string;
  created?: number;
  failed?: number;
  skipped?: number;
  errors?: string[];
}

export interface UploadOptions {
  createMissingUnits: boolean;
}

type BulkUploadFn = (
  planned: PlannedAppointmentRow[],
  onProgress?: (completed: number, total: number) => void
) => Promise<BulkUploadResult>;

const plural = (count: number) => (count !== 1 ? "s" : "");

function buildMessage(created: number, duplicates: number, skippedMissing: number, invalid: number, failed: number) {
  const parts = [
    `Uploaded ${created} appointment${plural(created)}`,
    ...(duplicates > 0 ? [`skipped ${duplicates} duplicate${plural(duplicates)}`] : []),
    ...(skippedMissing > 0 ? [`skipped ${skippedMissing} row${plural(skippedMissing)} with missing units`] : []),
    ...(invalid > 0 ? [`${invalid} invalid row${plural(invalid)} not imported`] : []),
    ...(failed > 0 ? [`${failed} failed`] : []),
  ];

  return `${parts.join(", ")}.`;
}

/**
 * Runs the pre-import plan check for a parsed bulk upload file and holds its
 * state. Stale responses (from a previously selected file) are discarded.
 */
export function useBulkUploadPlan<T>(planUpload: (rows: T[]) => Promise<BulkUploadPlan>) {
  const [plan, setPlan] = useState<BulkUploadPlan | null>(null);
  const [isPlanning, setIsPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [createMissingUnits, setCreateMissingUnits] = useState(false);
  const requestIdRef = useRef(0);

  const resetPlan = useCallback(() => {
    requestIdRef.current++;
    setPlan(null);
    setIsPlanning(false);
    setPlanError(null);
    setCreateMissingUnits(false);
  }, []);

  const runPlanCheck = useCallback(
    async (rows: T[]) => {
      const requestId = ++requestIdRef.current;
      setPlan(null);
      setIsPlanning(true);
      setPlanError(null);
      setCreateMissingUnits(false);

      try {
        const result = await planUpload(rows);
        if (requestIdRef.current !== requestId) return;
        setPlan(result);
      } catch (error) {
        if (requestIdRef.current !== requestId) return;
        console.error("Bulk upload validation failed:", error);
        setPlanError(error instanceof Error ? error.message : "Unknown error");
      } finally {
        if (requestIdRef.current === requestId) {
          setIsPlanning(false);
        }
      }
    },
    [planUpload]
  );

  const importCount = plan ? plan.ready.length + (createMissingUnits ? plan.missingUnits.length : 0) : 0;

  return { plan, isPlanning, planError, createMissingUnits, setCreateMissingUnits, resetPlan, runPlanCheck, importCount };
}

export function useBulkAppointmentUpload(bulkUpload: BulkUploadFn) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);

  const uploadAppointments = async (plan: BulkUploadPlan, options: UploadOptions): Promise<UploadResult> => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const errors: string[] = [];
      let rowsToImport = plan.ready;
      let failCount = 0;

      if (options.createMissingUnits && plan.missingUnits.length > 0) {
        try {
          const unitResult = await createMissingUnitsForPlan(plan.missingUnits);
          rowsToImport = [...rowsToImport, ...unitResult.resolvedRows];
          errors.push(...unitResult.errors);
          failCount += plan.missingUnits.length - unitResult.resolvedRows.length;
        } catch (unitError) {
          // Unit creation failing (e.g. permissions) should not block the rows that are ready.
          errors.push(
            `Could not create missing units: ${unitError instanceof Error ? unitError.message : "Unknown error"}`
          );
          failCount += plan.missingUnits.length;
        }
      }

      const skippedMissingUnits = options.createMissingUnits ? 0 : plan.missingUnits.length;

      const importResult = await bulkUpload(rowsToImport, (completed, total) =>
        setUploadProgress(total === 0 ? 100 : Math.round((completed / total) * 100))
      );

      failCount += importResult.failed;
      errors.push(...importResult.errors);

      const result: UploadResult = {
        success: failCount === 0,
        message: buildMessage(
          importResult.created,
          plan.duplicates.length,
          skippedMissingUnits,
          plan.errors.length,
          failCount
        ),
        created: importResult.created,
        failed: failCount,
        skipped: plan.duplicates.length + skippedMissingUnits + plan.errors.length,
        errors: errors.length > 0 ? errors : undefined,
      };

      setUploadResult(result);
      return result;
    } catch (error) {
      const result: UploadResult = {
        success: false,
        message: error instanceof Error ? error.message : "Upload failed",
        created: 0,
        failed: plan.ready.length + (options.createMissingUnits ? plan.missingUnits.length : 0),
      };

      setUploadResult(result);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadResult(null);
  };

  return {
    uploadAppointments,
    isUploading,
    uploadProgress,
    uploadResult,
    resetUpload,
  };
}
